import React, { forwardRef, useLayoutEffect, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { Button } from "../../atoms/Button/index.js";
import {
  ALERT_GLYPH,
  ALERT_PARTS,
  alertVariants,
  type AlertShape,
  type AlertSurface,
  type AlertVariant,
} from "./alert-styles.js";

export { alertVariants };
export type { AlertShape, AlertSurface, AlertVariant };

/* ---------------- Alert ---------------- */

/** An Alert's semantic colour: the shared status tones (packages/ui/CLAUDE.md
 *  C7). `accent` is not a tone. */
export type AlertTone = StatusTone;

interface AlertBaseProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  tone?: AlertTone;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  /**
   * The trailing action group, after the body. Secondary or ghost Buttons
   * only: an Alert never carries the region's primary (invariant 9).
   */
  actionsSlot?: React.ReactNode;
  /** Renders a 24px ghost X after the actions that calls this once per click. */
  onDismiss?: () => void;
  /** The X's accessible name. */
  dismissLabel?: string;
  /**
   * Give this when the notice comes and goes and the caller keeps it mounted,
   * and the Alert will fade itself out too. A caller that renders
   * `{flag && <Alert/>}` has already destroyed the node, so only the entrance
   * can animate — which is why this is opt-in rather than assumed.
   */
  open?: boolean;
}

/* `surface` and `shape` only mean something on a floating Alert, so the type
   refuses them anywhere else. */
type AlertVariantProps =
  | {
      /** `default` is today's box. `inline` sits in the text flow with no
       *  box (14px glyph, `text-dense-1` in the tone's text colour). `banner`
       *  is a full-bleed strip with `border-y` and no corner. `floating` is a
       *  notice over a canvas. */
      variant?: Exclude<AlertVariant, "floating">;
      surface?: never;
      shape?: never;
    }
  | {
      variant: "floating";
      /** The paint, from lib/surfaces.ts. `default` (the default) is the
       *  workspace paint; `glass` is the glass paint over a canvas. */
      surface?: AlertSurface;
      /** The corner (lib/shape.ts). `pill` (the default) is fully round;
       *  `rounded` is 8px. */
      shape?: AlertShape;
    };

export type AlertProps = AlertBaseProps & AlertVariantProps;

const AlertRoot = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  {
    tone = "info",
    title,
    children,
    icon,
    className,
    open,
    actionsSlot,
    onDismiss,
    dismissLabel = "Dismiss",
    variant = "default",
    surface,
    shape,
    ...rest
  },
  ref,
) {
  /* A closing banner is faded and takes no pointer events, but a link or button
     inside it stays in the TAB ORDER until it unmounts, so `inert` is what
     actually finishes the job (ADR-0091). Imperative, because React 18
     serialises `inert={false}` as the truthy string "false". */
  const innerRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    if (open === undefined) return;
    innerRef.current?.toggleAttribute("inert", !open);
  }, [open]);

  const Glyph = ALERT_GLYPH[tone];
  const parts = ALERT_PARTS[variant];

  return (
    <div
      ref={(node) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      role="alert"
      {...(open === undefined
        ? {}
        : {
            "data-motion-state": open
              ? ("open" as const)
              : ("closing" as const),
            ...(open ? {} : { "aria-hidden": "true" as const }),
          })}
      className={cn(
        alertVariants({ variant, tone, surface, shape }),
        className,
      )}
      {...rest}
    >
      <span className={parts.iconWrap}>
        {icon ?? <Glyph className={cn(parts.glyph, TONES[tone].text)} />}
      </span>
      <div className={parts.content}>
        {title && <div className={parts.title}>{title}</div>}
        {children && <div className={parts.body || undefined}>{children}</div>}
      </div>
      {actionsSlot && (
        <div className="flex shrink-0 items-center gap-2 self-center">
          {actionsSlot}
        </div>
      )}
      {onDismiss && (
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          aria-label={dismissLabel}
          onClick={onDismiss}
          className="-my-0.5 shrink-0"
        >
          <X aria-hidden className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
});

/**
 * A notice, `role="alert"` by default. A note that must not interrupt (one
 * that appears while someone types) passes `role="status"` over it.
 *
 * A region that failed to load uses `EmptyState variant="error"` through
 * `AsyncSection` or `DataTable` (`size="sm"` when small), never an Alert. An
 * Alert, `variant="inline"` included, is only for validation and notices
 * about content that did load. One home: packages/ui/CLAUDE.md, C7
 * "Design-system programme notes", the `EmptyState size="sm"` line.
 */
export const Alert = AlertRoot;
